import { preEncapOpenQualityIncidentBlockMessage } from "@/lib/qc/qualityIncidentGuards";

import type { PreEncapDevice, PreEncapDraftRow, PreEncapPottingSendFlow } from "./preEncapsulationApi";

export type PreEncapScanFeedbackTone = "error" | "warning" | "info";

export interface PreEncapScanFeedbackLine {
  tone: PreEncapScanFeedbackTone;
  message: string;
}

export function preEncapStationMismatchMessage(device: PreEncapDevice): string | null {
  if (device.stationId == null || device.routeStationId == null) {
    return null;
  }
  if (device.stationId === device.routeStationId) {
    return null;
  }
  const current = device.stationName?.trim() || `station ${device.stationId}`;
  const target = device.routeStationName?.trim() || `station ${device.routeStationId}`;
  return `Currently at ${current}; routing recommends ${target}.`;
}

export function preEncapDraftRowForSerial(
  draftRows: PreEncapDraftRow[],
  serial: string,
): PreEncapDraftRow | undefined {
  const upper = serial.toUpperCase();
  return draftRows.find((row) => row.serial != null && row.serial.toUpperCase() === upper);
}

function onDraftCartonSuffix(row: PreEncapDraftRow | undefined): string {
  if (row?.cartonNumber == null) {
    return "";
  }
  const product =
    row.productLabel != null && row.laborSku != null
      ? ` (${row.laborSku} · ${row.productLabel})`
      : row.productLabel != null
        ? ` (${row.productLabel})`
        : "";
  const cartonLabel = row.sendFlow === "rework" ? "rework carton" : "carton";
  return ` — already in ${cartonLabel} ${row.cartonNumber}${product}.`;
}

export function preEncapDraftCartonForDevice(
  device: PreEncapDevice,
  draftRows: PreEncapDraftRow[],
): { cartonNumber: number; flow: PreEncapPottingSendFlow } | null {
  const deviceLabel = device.deviceLabelSerial;
  const clampLabel = device.clampSerial;
  const deviceRow = deviceLabel != null ? preEncapDraftRowForSerial(draftRows, deviceLabel) : undefined;
  const clampRow = clampLabel != null ? preEncapDraftRowForSerial(draftRows, clampLabel) : undefined;
  const row = deviceRow ?? clampRow;
  if (row?.cartonNumber == null) {
    return null;
  }
  return {
    cartonNumber: row.cartonNumber,
    flow: row.sendFlow ?? "first_pass",
  };
}

export function preEncapAlreadyOnDraftMessage(
  device: PreEncapDevice,
  draftRows: PreEncapDraftRow[],
): string | null {
  const deviceLabel = device.deviceLabelSerial;
  const clampLabel = device.clampSerial;
  const deviceRow = deviceLabel != null ? preEncapDraftRowForSerial(draftRows, deviceLabel) : undefined;
  const clampRow = clampLabel != null ? preEncapDraftRowForSerial(draftRows, clampLabel) : undefined;
  const deviceOnDraft = deviceRow != null;
  const clampOnDraft = clampRow != null;
  if (deviceOnDraft && clampOnDraft) {
    return `Device label and clamp serial are already on the potting send draft.${onDraftCartonSuffix(deviceRow)}`;
  }
  if (deviceOnDraft) {
    const prefix =
      deviceRow?.sendFlow === "rework"
        ? "Device is already assigned to potting rework"
        : "Device label is already on the potting draft";
    return `${prefix}.${onDraftCartonSuffix(deviceRow)}`;
  }
  if (clampOnDraft) {
    return `Clamp serial is already on the potting draft.${onDraftCartonSuffix(clampRow)}`;
  }
  return null;
}

/** Whether a successful scan should auto-add to the potting draft (no manual button). */
export function canAutoAddPreEncapScanToDraft(
  device: PreEncapDevice,
  draftRows: PreEncapDraftRow[],
): { kind: "device" | "clamp"; serial: string; buildOrderId?: number | null } | null {
  if (device.scanBlockedReason?.trim()) {
    return null;
  }
  if (preEncapOpenQualityIncidentBlockMessage(device) != null) {
    return null;
  }
  if (device.openPottingReworkId != null && device.openPottingReworkId > 0) {
    return null;
  }
  if (device.matchKind === "device_label") {
    if (device.routeDestination !== "potting" || device.draftAddRejection != null) {
      return null;
    }
    const deviceLabel = device.deviceLabelSerial;
    if (deviceLabel == null) {
      return null;
    }
    if (preEncapDraftRowForSerial(draftRows, deviceLabel) != null) {
      return null;
    }
    return { kind: "device", serial: deviceLabel };
  }
  if (device.matchKind === "clamp") {
    const clampSerial = device.clampSerial;
    if (clampSerial == null || device.buildOrderId == null) {
      return null;
    }
    if (preEncapDraftRowForSerial(draftRows, clampSerial) != null) {
      return null;
    }
    return { kind: "clamp", serial: clampSerial, buildOrderId: device.buildOrderId };
  }
  return null;
}

/**
 * FI-5A / DM-1 device labels that route to Conformal Coat instead of potting draft.
 */
export function preEncapConformalCoatDeviceLabelRoute(device: PreEncapDevice): boolean {
  return device.routeDestination === "conformal_coat" && device.matchKind === "device_label";
}

/**
 * Scan succeeded and advanced routing, but the unit must not join the potting draft
 * (for example FI-5A / DM-1 device labels routed to Conformal Coat).
 */
export function preEncapIsDivertedSuccessScan(
  device: PreEncapDevice,
  input?: { stationAdvanced?: boolean },
): boolean {
  if (input?.stationAdvanced === false) {
    return false;
  }
  if (device.scanBlockedReason?.trim()) {
    return false;
  }
  if (preEncapOpenQualityIncidentBlockMessage(device) != null) {
    return false;
  }
  return preEncapConformalCoatDeviceLabelRoute(device);
}

/** Hard stop when the device is already at the routed destination (no station advance). */
export function preEncapAlreadyAtRouteDestinationMessage(
  device: PreEncapDevice,
  input: { stationAdvanced: boolean },
): string | null {
  if (input.stationAdvanced) {
    return null;
  }
  if (device.scanBlockedReason?.trim()) {
    return null;
  }
  if (preEncapOpenQualityIncidentBlockMessage(device) != null) {
    return null;
  }
  if (!preEncapConformalCoatDeviceLabelRoute(device)) {
    return null;
  }
  const station = device.routeStationName?.trim() || device.stationName?.trim() || "Conformal Coat";
  return `Device is already at ${station}. Do not add to a potting carton.`;
}

/** Amber-banner copy after a diverted success scan (not potting carton success). */
export function preEncapDivertedSuccessMessage(
  device: PreEncapDevice,
  input: { stationAdvanced: boolean },
): string {
  const packing = device.packingInstruction?.trim();
  if (input.stationAdvanced && packing != null && packing !== "") {
    return packing;
  }
  const station = device.routeStationName?.trim() || "Conformal Coat";
  return [
    `Scan recorded — routed to ${station}.`,
    "Do not add to a potting carton.",
  ].join("\n");
}

/** Hard stop: calibration/QC/shipment block, or this scan cannot join the potting draft. */
export function preEncapScanNeedsFailureSound(
  device: PreEncapDevice,
  input?: { stationAdvanced?: boolean },
): boolean {
  if (preEncapAlreadyAtRouteDestinationMessage(device, {
    stationAdvanced: input?.stationAdvanced ?? true,
  }) != null) {
    return true;
  }
  if (device.scanBlockedReason?.trim()) {
    return true;
  }
  if (preEncapOpenQualityIncidentBlockMessage(device) != null) {
    return true;
  }
  if (preEncapIsDivertedSuccessScan(device)) {
    return false;
  }
  if (device.draftAddRejection?.trim()) {
    return true;
  }
  return false;
}

/** Operator-facing hard stop copy when scan returns a device row but cannot proceed. */
export function preEncapScanFailureMessage(device: PreEncapDevice): string | null {
  const scanBlock = device.scanBlockedReason ?? preEncapOpenQualityIncidentBlockMessage(device);
  if (scanBlock != null) {
    return scanBlock;
  }
  if (preEncapIsDivertedSuccessScan(device)) {
    return null;
  }
  const draftRejection = device.draftAddRejection?.trim();
  if (draftRejection != null && draftRejection !== "") {
    return draftRejection;
  }
  return null;
}

export function buildPreEncapScanFeedbackLines(
  device: PreEncapDevice,
  draftRows: PreEncapDraftRow[],
): PreEncapScanFeedbackLine[] {
  const lines: PreEncapScanFeedbackLine[] = [];

  const scanBlock = device.scanBlockedReason ?? preEncapOpenQualityIncidentBlockMessage(device);
  const hardBlock = scanBlock != null;
  if (scanBlock != null) {
    lines.push({ tone: "error", message: scanBlock });
  }

  const alreadyOnDraft = preEncapAlreadyOnDraftMessage(device, draftRows);
  const packingInstruction = device.packingInstruction?.trim() ?? null;
  if (!hardBlock && alreadyOnDraft == null && packingInstruction != null) {
    const tone: PreEncapScanFeedbackTone = preEncapIsDivertedSuccessScan(device) ? "warning" : "info";
    lines.push({ tone, message: packingInstruction });
  }

  if (alreadyOnDraft != null) {
    lines.push({ tone: "warning", message: alreadyOnDraft });
  }

  return lines;
}
